import { useEffect, useRef, useState } from 'react';

export interface MenuItem {
  /** Id paa den fane, punktet aabner (se tabs/registry). */
  kind: string;
  label: string;
  params?: Record<string, string>;
  /** Endnu ikke bygget: vises graat og kan ikke klikkes. */
  disabled?: boolean;
}

export interface MenuColumn {
  /** Versal-overskrift over kolonnen. */
  title: string;
  items: MenuItem[];
}

export interface MegaMenuProps {
  columns: MenuColumn[];
  onSelect: (item: MenuItem) => void;
  /** Id paa den aktive fanes side, saa punktet kan markeres. */
  activeKind?: string | null;
}

/**
 * "Menu"-knappen i topbaren og dens mega-dropdown: ét hvidt panel under knappen
 * med en kolonne pr. område og smaa versal-overskrifter. Et klik paa et punkt
 * aabner (eller aktiverer) den tilsvarende top-fane – samme model som
 * `showMenu` + `.dropdown` + `openPage()` i `HBPlanner v2.html`.
 */
export function MegaMenu({ columns, onSelect, activeKind }: MegaMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocumentDown(event: MouseEvent) {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocumentDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocumentDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="menu-wrapper" ref={wrapper}>
      <button
        type="button"
        className="btn-menu"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        Menu
      </button>

      {open && (
        <div className="mega-menu" role="menu" aria-label="Hovedmenu">
          {columns.map((column) => (
            <div className="mega-menu-col" key={column.title}>
              <div className="mega-menu-title">{column.title}</div>
              {column.items.map((item) => (
                <button
                  type="button"
                  role="menuitem"
                  key={`${column.title}-${item.label}`}
                  className={`mega-menu-item${item.kind === activeKind ? ' active' : ''}${
                    item.disabled ? ' disabled' : ''
                  }`}
                  disabled={item.disabled}
                  onClick={() => {
                    setOpen(false);
                    onSelect(item);
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
